import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './GeneratorHeader.css';
import { ContentCopy, Share, FileUpload, DeleteOutline, Add, GetApp, Favorite, FavoriteBorder, VisibilityOutlined } from '@mui/icons-material';
import { formatCount } from '../../utils/formatCount';
import DropdownMenu from '../utils/DropdownMenu';
import Tooltip from '../utils/Tooltip';
import DisplayModeToggle from './DisplayModeToggle';

const MOBILE_MAX = '(max-width: 60rem)';

const TXT_BUILD_PLACEHOLDER = { fr: 'nom du stuff', en: 'build name', de: 'Build-Name', es: 'nombre de la build', pt: 'nome da build' };
const TXT_COPY = { fr: 'Dupliquer', en: 'Duplicate', de: 'Duplizieren', es: 'Duplicar', pt: 'Duplicar' };
const TXT_CREATE = { fr: 'Nouveau stuff', en: 'New build', de: 'Neuer Build', es: 'Nueva build', pt: 'Nova build' };
const TXT_SHARE = { fr: 'Partager', en: 'Share', de: 'Teilen', es: 'Compartir', pt: 'Compartilhar' };
const TXT_EXPORT = { fr: 'Exporter vers Dofusbook', en: 'Export to Dofusbook', de: 'Nach Dofusbook exportieren', es: 'Exportar a Dofusbook', pt: 'Exportar para o Dofusbook' };
const TXT_SHARE_BUSY = { fr: 'Création du lien…', en: 'Creating link…', de: 'Link wird erstellt…', es: 'Creando el enlace…', pt: 'Criando o link…' };
const TXT_DELETE_BUILD = { fr: 'Supprimer', en: 'Delete', de: 'Löschen', es: 'Eliminar', pt: 'Excluir' };
const TXT_MENU = { fr: 'Menu du stuff', en: 'Build menu', de: 'Build-Menü', es: 'Menú de la build', pt: 'Menu da build' };
const TXT_IMPORT = { fr: 'Importer le stuff', en: 'Import build', de: 'Build importieren', es: 'Importar la build', pt: 'Importar a build' };
const TXT_UNKNOWN = { fr: 'Inconnu', en: 'Unknown', de: 'Unbekannt', es: 'Desconocido', pt: 'Desconhecido' };
const TXT_LIKE = { fr: 'Liker le stuff', en: 'Like build', de: 'Build liken', es: 'Dar like a la build', pt: 'Curtir a build' };
const TXT_UNLIKE = { fr: 'Retirer le like', en: 'Unlike build', de: 'Like entfernen', es: 'Quitar el like', pt: 'Remover a curtida' };
const TXT_VIEWS = { fr: 'vues', en: 'views', de: 'Aufrufe', es: 'visitas', pt: 'visualizações' };
const TXT_METRICS = { fr: 'Statistiques du stuff', en: 'Build metrics', de: 'Build-Statistiken', es: 'Estadísticas de la build', pt: 'Estatísticas da build' };
function useMediaMax60rem() {
    const [matches, setMatches] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MAX).matches
    );
    useEffect(() => {
        const m = window.matchMedia(MOBILE_MAX);
        const onChange = () => setMatches(m.matches);
        m.addEventListener('change', onChange);
        return () => m.removeEventListener('change', onChange);
    }, []);
    return matches;
}

const GeneratorHeader = ({
    lang,
    buildName,
    setBuildName,
    buildNameInputRef,
    isGenerationActive,
    onCopyBuild,
    onCreateBuild,
    onShareBuild,
    onExportBuild,
    onDeleteBuild,
    isCopyDisabled,
    isShareDisabled,
    isShareBusy = false,
    isExportDisabled = false,
    isDeleteDisabled,
    headerStartContent = null,
    isReadOnly = false,
    isPublicReadOnly = false,
    onImportBuild,
    displayMode = 'generator',
    onDisplayModeChange,
    showModeSelector = true,
    owner = null,
    metrics = null,
    viewerHasLiked = false,
    onToggleLike,
    isLikeDisabled = false,
}) => {
    const placeholder = TXT_BUILD_PLACEHOLDER[lang] || TXT_BUILD_PLACEHOLDER.en;
    const copyTitle = TXT_COPY[lang] || TXT_COPY.en;
    const createTitle = TXT_CREATE[lang] || TXT_CREATE.en;
    const shareLabel = TXT_SHARE[lang] || TXT_SHARE.en;
    const shareBusyTitle = TXT_SHARE_BUSY[lang] || TXT_SHARE_BUSY.en;
    const exportLabel = TXT_EXPORT[lang] || TXT_EXPORT.en;
    const deleteBuildLabel = TXT_DELETE_BUILD[lang] || TXT_DELETE_BUILD.en;
    const importLabel = TXT_IMPORT[lang] || TXT_IMPORT.en;
    const menuLabel = TXT_MENU[lang] || TXT_MENU.en;
    const likeLabel = viewerHasLiked ? (TXT_UNLIKE[lang] || TXT_UNLIKE.en) : (TXT_LIKE[lang] || TXT_LIKE.en);
    const ownerName = owner?.display_name || owner?.public_slug || TXT_UNKNOWN[lang] || TXT_UNKNOWN.en;
    const likes = formatCount(metrics?.likes, lang);
    // The badge is compact ("3,4k"); the tooltip it opens has room for the
    // exact figure, which is the one a creator actually wants to read.
    const views = formatCount(metrics?.views, lang);
    const exactViews = Number(metrics?.views || 0).toLocaleString();
    const viewsLabel = `${exactViews} ${TXT_VIEWS[lang] || TXT_VIEWS.en}`;
    const showOwner = !!owner;
    const showMetrics = !!metrics;
    const isMobileLayout = useMediaMax60rem();

    const handleShare = useCallback(() => {
        if (isShareDisabled || isShareBusy) return;
        onShareBuild?.();
    }, [isShareDisabled, isShareBusy, onShareBuild]);

    const menuItems = useMemo(() => {
        const createItem = {
            label: createTitle,
            icon: <Add fontSize="small" />,
            onClick: onCreateBuild,
            disabled: isCopyDisabled,
        };
        const copyItem = {
            label: copyTitle,
            icon: <ContentCopy fontSize="small" />,
            onClick: onCopyBuild,
            disabled: isCopyDisabled,
        };
        const shareItem = {
            label: shareLabel,
            icon: <Share fontSize="small" />,
            onClick: handleShare,
            disabled: isShareDisabled || isShareBusy,
            tooltip: isShareBusy ? shareBusyTitle : undefined,
        };
        const exportItem = {
            label: exportLabel,
            icon: <FileUpload fontSize="small" />,
            onClick: onExportBuild,
            disabled: isExportDisabled || !onExportBuild,
        };
        const deleteItem = {
            label: deleteBuildLabel,
            icon: <DeleteOutline fontSize="small" />,
            onClick: onDeleteBuild,
            disabled: isDeleteDisabled,
        };
        // Someone else's build: deleting it is not on offer, and duplicating it
        // is what the import button next door already does — but starting a
        // build of one's own and sharing this one still are.
        if (isPublicReadOnly) return [createItem, shareItem, exportItem];
        if (isMobileLayout) return [createItem, copyItem, shareItem, exportItem, deleteItem];
        return [createItem, deleteItem];
    }, [
        isMobileLayout,
        createTitle,
        copyTitle,
        shareLabel,
        shareBusyTitle,
        exportLabel,
        deleteBuildLabel,
        isCopyDisabled,
        isShareDisabled,
        isShareBusy,
        isExportDisabled,
        isDeleteDisabled,
        isPublicReadOnly,
        onCreateBuild,
        onCopyBuild,
        onDeleteBuild,
        onExportBuild,
        handleShare,
    ]);

    const modeSelector = showModeSelector && !isPublicReadOnly ? (
        <DisplayModeToggle
            lang={lang}
            value={displayMode}
            onChange={onDisplayModeChange}
            disabled={isGenerationActive}
        />
    ) : null;

    const nameElement = isPublicReadOnly ? (
        <h1 className="generator-header-build-name-readonly">
            {buildName || placeholder}
        </h1>
    ) : (
        <input
            ref={buildNameInputRef}
            className="build-name-input generator-header-build-name-input"
            type="text"
            value={buildName}
            onChange={e => setBuildName(e.target.value)}
            placeholder={placeholder}
            disabled={isGenerationActive || isReadOnly}
        />
    );

    const ownerElement = showOwner ? (
        <div className="generator-header-owner">
            {owner?.avatar_url ? (
                <img className="generator-header-owner-avatar" src={owner.avatar_url} alt="" />
            ) : (
                <span className="generator-header-owner-avatar generator-header-owner-avatar--fallback" aria-hidden>
                    {ownerName.slice(0, 1).toUpperCase()}
                </span>
            )}
            <span className="generator-header-owner-name">{ownerName}</span>
        </div>
    ) : null;

    const metricsElement = showMetrics ? (
        <div className="generator-header-metrics" aria-label={TXT_METRICS[lang] || TXT_METRICS.en}>
            <Tooltip content={viewsLabel}>
                <span className="generator-header-metric generator-header-view-metric" aria-label={viewsLabel}>
                    <VisibilityOutlined fontSize="small" />
                    <span>{views}</span>
                </span>
            </Tooltip>
            <Tooltip content={likeLabel}>
                <button
                    type="button"
                    className={`alt generator-header-metric generator-header-like-button${viewerHasLiked ? ' active' : ''}`}
                    onClick={onToggleLike}
                    disabled={isLikeDisabled || !onToggleLike}
                    aria-label={likeLabel}
                    aria-pressed={viewerHasLiked}
                >
                    {viewerHasLiked ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                    <span>{likes}</span>
                </button>
            </Tooltip>
        </div>
    ) : null;

    const importButton = isPublicReadOnly ? (
        <button
            type="button"
            className="generator-header-import-btn"
            onClick={onImportBuild}
            disabled={isGenerationActive}
        >
            <GetApp fontSize="small" />
            <span>{importLabel}</span>
        </button>
    ) : null;

    const menuDropdown = (
        <DropdownMenu
            items={menuItems}
            dropdownClassName="generator-header-dropdown"
            menuClassName="generator-header-dropdown-panel"
            showButtonLabel={false}
            highlightActiveItem={false}
            triggerIcon="kebab"
            buttonContent={(
                <span className="generator-header-menu-sr">{menuLabel}</span>
            )}
            buttonTooltip={menuLabel}
        />
    );

    if (isMobileLayout) {
        // Row 1: byline + metrics; row 2: name + actions.
        return (
            <div className="generator-header generator-header--mobile">
                {(ownerElement || metricsElement) ? (
                    <div className="generator-header-meta-row">
                        {ownerElement}
                        {metricsElement}
                    </div>
                ) : null}
                <div className="generator-header-title-row">
                    {headerStartContent}
                    {nameElement}
                    {importButton}
                    {menuDropdown}
                    {modeSelector}
                </div>
            </div>
        );
    }

    return (
        <div className="generator-header row">
            <div className="generator-header-left">
                {headerStartContent}
                <div className="generator-header-build-name-wrap">
                    {nameElement}
                    {ownerElement}
                </div>
            </div>
            <div className="generator-header-center" />
            <div className="generator-header-actions">
                {metricsElement}
                {isPublicReadOnly ? importButton : (
                    <div className="generator-header-actions-desktop">
                        <Tooltip content={copyTitle}>
                            <button
                                type="button"
                                className="alt icon-only"
                                onClick={onCopyBuild}
                                disabled={isCopyDisabled}
                                aria-label={copyTitle}
                            >
                                <ContentCopy fontSize="small" />
                            </button>
                        </Tooltip>
                        <Tooltip content={isShareBusy ? shareBusyTitle : shareLabel}>
                            <button
                                type="button"
                                className="alt icon-only"
                                onClick={handleShare}
                                disabled={isShareDisabled || isShareBusy}
                                aria-label={shareLabel}
                            >
                                <Share fontSize="small" />
                            </button>
                        </Tooltip>
                        <Tooltip content={exportLabel}>
                            <button
                                type="button"
                                className="alt icon-only"
                                onClick={onExportBuild}
                                disabled={isExportDisabled || !onExportBuild}
                                aria-label={exportLabel}
                            >
                                <FileUpload fontSize="small" />
                            </button>
                        </Tooltip>
                    </div>
                )}
                {menuDropdown}
                {modeSelector}
            </div>
        </div>
    );
};

export default GeneratorHeader;


